import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api";

const CreateUser = () => {
  const navigate = useNavigate();

  const [userData, setUserData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    password: "",
    birthday: "",
    location: "",
    role: "visiteur",
    isAdmin: false,
    avatar: null,
  });

  const [avatarPreview, setAvatarPreview] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userData.avatar) {
      setAvatarPreview(null);
      return;
    }

    const objectUrl = URL.createObjectURL(userData.avatar);
    setAvatarPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [userData.avatar]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setUserData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0] || null;

    setUserData((prevData) => ({
      ...prevData,
      avatar: file,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const formData = new FormData();

      formData.append("firstname", userData.firstname);
      formData.append("lastname", userData.lastname);
      formData.append("email", userData.email);
      formData.append("password", userData.password);
      formData.append("birthday", userData.birthday);
      formData.append("location", userData.location);
      formData.append("role", userData.role);
      formData.append("isAdmin", userData.isAdmin ? "true" : "false");

      if (userData.avatar instanceof File) {
        formData.append("avatar", userData.avatar);
      }

      await api.post("/admin/users", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      navigate("/dashboard/users");
    } catch (err) {
      console.error(err);

      if (err.response?.data?.errors) {
        const msgs = Object.values(err.response.data.errors).flat().join(" ");
        setError(msgs);
      } else {
        setError(
          err.response?.data?.message ||
            err.response?.data?.title ||
            "Erreur lors de la création de l'utilisateur."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-10 text-slate-100">
      <div className="mx-auto max-w-4xl">
        <section className="mb-8 overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-green-900 text-white shadow-sm">
          <div className="p-8 md:p-10">
            <p className="mb-3 inline-flex rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-green-100">
              Dashboard utilisateurs
            </p>

            <h1 className="mb-3 text-3xl font-bold md:text-4xl">
              Créer un utilisateur
            </h1>

            <p className="max-w-2xl text-sm leading-7 text-slate-200 md:text-base">
              Ajoutez un nouvel utilisateur avec ses informations, son rôle, sa
              localité, son avatar et ses permissions.
            </p>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-sm">
          {error && (
            <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6">
              <h2 className="mb-5 text-lg font-semibold text-white">Avatar</h2>

              {avatarPreview && (
                <div className="mb-6 flex flex-col items-center">
                  <p className="mb-3 text-sm font-medium text-slate-300">
                    Aperçu avatar
                  </p>
                  <img
                    src={avatarPreview}
                    alt="Avatar aperçu"
                    className="h-28 w-28 rounded-full border-4 border-slate-700 object-cover shadow-lg"
                  />
                </div>
              )}

              <div>
                <label
                  htmlFor="avatar"
                  className="mb-2 block text-sm font-medium text-slate-200"
                >
                  Avatar
                </label>
                <input
                  type="file"
                  id="avatar"
                  name="avatar"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 file:mr-4 file:rounded-lg file:border-0 file:bg-white file:px-4 file:py-2 file:text-sm file:font-medium file:text-slate-950 hover:file:bg-slate-200"
                />
              </div>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6">
              <h2 className="mb-5 text-lg font-semibold text-white">
                Informations personnelles
              </h2>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="firstname" className="mb-2 block text-sm font-medium text-slate-200">
                    Prénom
                  </label>
                  <input
                    type="text"
                    id="firstname"
                    name="firstname"
                    value={userData.firstname}
                    onChange={handleChange}
                    required
                    className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="lastname" className="mb-2 block text-sm font-medium text-slate-200">
                    Nom
                  </label>
                  <input
                    type="text"
                    id="lastname"
                    name="lastname"
                    value={userData.lastname}
                    onChange={handleChange}
                    required
                    className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-200">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={userData.email}
                    onChange={handleChange}
                    required
                    className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="mb-2 block text-sm font-medium text-slate-200">
                    Mot de passe
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={userData.password}
                    onChange={handleChange}
                    required
                    className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="birthday" className="mb-2 block text-sm font-medium text-slate-200">
                    Anniversaire
                  </label>
                  <input
                    type="date"
                    id="birthday"
                    name="birthday"
                    value={userData.birthday}
                    onChange={handleChange}
                    className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="location" className="mb-2 block text-sm font-medium text-slate-200">
                    Localité
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={userData.location}
                    onChange={handleChange}
                    placeholder="Ex : Bruxelles"
                    required
                    className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6">
              <h2 className="mb-5 text-lg font-semibold text-white">
                Paramètres du compte
              </h2>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="role" className="mb-2 block text-sm font-medium text-slate-200">
                    Rôle
                  </label>
                  <select
                    name="role"
                    id="role"
                    value={userData.role}
                    onChange={handleChange}
                    className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 focus:border-blue-500 focus:outline-none"
                  >
                    <option value="visiteur">Visiteur</option>
                    <option value="mannequin">Mannequin</option>
                    <option value="photographe">Photographe</option>
                    <option value="organisateur">Organisateur d'évènement</option>
                  </select>
                </div>

                <div className="mt-6">
                  <label className="inline-flex items-center gap-3 text-sm text-slate-200">
                    <input
                      type="checkbox"
                      id="isAdmin"
                      name="isAdmin"
                      checked={userData.isAdmin}
                      onChange={handleChange}
                      className="h-4 w-4 rounded border-slate-600 bg-slate-900 accent-blue-500"
                    />
                    <span>Administrateur</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-green-600 px-6 py-3 font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {loading ? "Création..." : "Créer l'utilisateur"}
              </button>

              <button
                type="button"
                onClick={() => navigate("/dashboard/users")}
                className="w-full rounded-xl border border-slate-700 px-6 py-3 font-semibold text-slate-200 transition hover:bg-slate-800 sm:w-auto"
              >
                Annuler
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default CreateUser;